// The product video: plays (muted, looping) while it is on screen, unless the visitor prefers reduced motion
// or paused it. The button always works, and the transcript's times seek to that shot and play it.
import { afterFirstPaint } from './after-paint.ts';

export function initFilm(): void {
  const video = document.querySelector<HTMLVideoElement>('[data-film]');
  const toggle = document.querySelector<HTMLButtonElement>('[data-film-toggle]');
  if (!video || !toggle) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  let userPaused = false;
  // Pressing play (or a chapter) is a choice to watch, which reduced motion doesn't override.
  let userPlayed = false;
  let visible = false;

  const label = () => {
    const playing = !video.paused && !video.ended;
    toggle.textContent = playing ? 'Pause' : 'Play';
    toggle.setAttribute('aria-label', playing ? 'Pause the video' : 'Play the video');
    toggle.closest('.film__frame')?.toggleAttribute('data-playing', playing);
  };
  const play = () => {
    video.preload = 'auto';
    void video.play().catch(() => label());
  };
  const sync = () => {
    const auto = userPlayed || !reduce.matches;
    if (visible && !userPaused && auto) play();
    else if (!video.paused && (!visible || !auto)) video.pause();
  };

  video.addEventListener('play', label);
  video.addEventListener('pause', label);
  toggle.addEventListener('click', () => {
    if (video.paused) {
      userPaused = false;
      userPlayed = true;
      play();
    } else {
      userPaused = true;
      video.pause();
    }
  });
  reduce.addEventListener('change', sync);

  for (const link of document.querySelectorAll<HTMLAnchorElement>('[data-seek]')) {
    link.addEventListener('click', (e) => {
      const t = Number(link.dataset.seek);
      if (!Number.isFinite(t)) return;
      e.preventDefault();
      if (video.readyState >= HTMLMediaElement.HAVE_METADATA) video.currentTime = t;
      else video.addEventListener('loadedmetadata', () => (video.currentTime = t), { once: true });
      userPaused = false;
      userPlayed = true;
      play();
      video.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'center' });
      toggle.focus({ preventScroll: true });
    });
  }

  // The poster loads as the film approaches, and never before the first screen has painted.
  const poster = video.dataset.poster;
  afterFirstPaint(() => {
    if (poster && 'IntersectionObserver' in window) {
      const near = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          video.poster = poster;
          near.disconnect();
        },
        { rootMargin: '600px 0px' },
      );
      near.observe(video);
    } else if (poster) video.poster = poster;
  });
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        visible = entries.some((e) => e.isIntersecting);
        sync();
      },
      { threshold: 0.35 },
    ).observe(video);
  }
  label();
}
